"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarIcon } from "@/components/Icons";
import { DatePickerSheet, pickerHost, useDateFormats } from "@/components/DatePickerSheet";
import { useI18n } from "@/i18n/I18nProvider";
import { INTL_LOCALE } from "@/i18n/config";
import { nightsBetween } from "@/lib/date-range";

/**
 * A single date for the staff app (filters, a valid-from, a session day), picked in the same calendar sheet as the
 * booking site. `value` / `onChange` use ISO dates ("" = empty), like the native input it replaces.
 */
export function DateField({
  value,
  onChange,
  min,
  max,
  placeholder,
  clearable = false,
  title,
  ariaLabel,
  style,
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  clearable?: boolean;
  title?: string;
  ariaLabel?: string;
  style?: React.CSSProperties;
  className?: string;
}) {
  const { t } = useI18n();
  const fmt = useDateFormats();
  const [host, setHost] = useState<Element | null>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const setOpen = (on: boolean) => setHost(on ? pickerHost(ref.current) : null);
  const showClear = clearable && Boolean(value);

  return (
    <div className="dp-field-wrap" style={style}>
      <button
        ref={ref}
        type="button"
        className={`form-control dp-field ${className}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={ariaLabel ? `${ariaLabel}: ${value ? fmt.full(value) : placeholder || t("common.datePicker.pick")}` : undefined}
      >
        <span className="dp-field-icon"><CalendarIcon size={14} /></span>
        <span className={`dp-field-text${value ? "" : " is-empty"}`}>{value ? fmt.short(value) : placeholder || t("common.datePicker.pick")}</span>
      </button>
      {showClear && (
        <button type="button" className="dp-field-clear" onClick={() => onChange("")} aria-label={t("common.datePicker.clear")}>{"✕"}</button>
      )}
      {host && (
        <DatePickerSheet
          mode="single"
          from={value}
          min={min}
          max={max}
          title={title || ariaLabel || t("common.datePicker.titleSingle")}
          clearable={clearable}
          host={host}
          onApply={(d) => {
            onChange(d);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * A stay or block of nights (check-in → check-out, start → end): one field, one calendar, the nights counted.
 * The end is always after the start.
 */
export function DateRangeField({
  start,
  end,
  onChange,
  min,
  max,
  startLabel,
  endLabel,
  title,
  showNights = true,
  style,
}: {
  start: string;
  end: string;
  onChange: (start: string, end: string) => void;
  min?: string;
  max?: string;
  startLabel: string;
  endLabel: string;
  title?: string;
  showNights?: boolean;
  style?: React.CSSProperties;
}) {
  const { t } = useI18n();
  const fmt = useDateFormats();
  const [host, setHost] = useState<Element | null>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const setOpen = (on: boolean) => setHost(on ? pickerHost(ref.current) : null);
  const nights = nightsBetween(start, end);
  const text = start && end ? `${fmt.short(start)}  →  ${fmt.short(end)}` : start ? `${fmt.short(start)}  →  …` : "";

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="form-control dp-field"
        style={style}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`${startLabel} – ${endLabel}: ${text || t("common.datePicker.pickRange")}`}
      >
        <span className="dp-field-icon"><CalendarIcon size={14} /></span>
        <span className={`dp-field-text${text ? "" : " is-empty"}`}>{text || t("common.datePicker.pickRange")}</span>
        {showNights && nights > 0 && <span className="dp-field-nights">{t("common.datePicker.nights", { count: nights })}</span>}
      </button>
      {host && (
        <DatePickerSheet
          mode="range"
          from={start}
          to={end}
          min={min}
          max={max}
          title={title || t("common.datePicker.titleRange")}
          startLabel={startLabel}
          endLabel={endLabel}
          host={host}
          onApply={(a, b) => {
            onChange(a, b);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** A month (YYYY-MM) picked from a year of month buttons, in the same sheet style. */
export function MonthField({ value, onChange, style }: { value: string; onChange: (value: string) => void; style?: React.CSSProperties }) {
  const { t, locale } = useI18n();
  const [host, setHost] = useState<Element | null>(null);
  const open = host !== null;
  const setOpen = (on: boolean) => setHost(on ? pickerHost(ref.current) : null);
  const [year, setYear] = useState(() => Number(value.slice(0, 4)) || new Date().getFullYear());
  const ref = useRef<HTMLButtonElement>(null);
  const tag = INTL_LOCALE[locale];
  const label = (ym: string) => new Intl.DateTimeFormat(tag, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${ym}-01T00:00:00Z`));
  const short = (m: number) => new Intl.DateTimeFormat(tag, { month: "short", timeZone: "UTC" }).format(new Date(Date.UTC(2024, m, 1))).replace(/\.$/, "");
  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="form-control dp-field"
        style={{ width: "auto", ...style }}
        onClick={() => {
          setYear(Number(value.slice(0, 4)) || new Date().getFullYear());
          setOpen(true);
        }}
        aria-haspopup="dialog"
      >
        <span className="dp-field-icon"><CalendarIcon size={14} /></span>
        <span className="dp-field-text" style={{ textTransform: "capitalize" }}>{value ? label(value) : t("common.datePicker.pickMonth")}</span>
      </button>
      {open &&
        createPortal(
          <MonthSheet
            title={t("common.datePicker.titleMonth")}
            year={year}
            setYear={setYear}
            value={value}
            short={short}
            onPick={(ym) => {
              onChange(ym);
              close();
            }}
            onClose={close}
          />,
          document.body
        )}
    </>
  );
}

function MonthSheet({ title, year, setYear, value, short, onPick, onClose }: { title: string; year: number; setYear: (y: number) => void; value: string; short: (m: number) => string; onPick: (ym: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const thisMonth = new Date().toISOString().slice(0, 7);
  return (
    <div className="dp-back" onClick={onClose}>
      <div className="dp dp-single" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="dp-head">
          <strong>{title}</strong>
          <button type="button" className="dp-x" onClick={onClose} aria-label={t("common.datePicker.close")}>{"✕"}</button>
        </div>
        <div className="dp-nav">
          <button type="button" onClick={() => setYear(year - 1)} aria-label={t("common.datePicker.prevYear")}>{"‹"}</button>
          <button type="button" onClick={() => setYear(year + 1)} aria-label={t("common.datePicker.nextYear")}>{"›"}</button>
        </div>
        <div className="dp-title">{year}</div>
        <div className="dp-month-grid">
          {Array.from({ length: 12 }, (_, m) => {
            const ym = `${year}-${String(m + 1).padStart(2, "0")}`;
            return (
              <button key={ym} type="button" className={`dp-day${ym === value ? " is-start" : ""}${ym === thisMonth ? " is-today" : ""}`} onClick={() => onPick(ym)}>
                {short(m)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
